<template>
  <div class="cp" role="group" :aria-label="t('player.controls.chapters')">
    <div v-if="player.chapters.value.length > 0" class="cp__items">
      <button
        v-for="(chapter, index) in player.chapters.value"
        :key="index"
        class="cp__item"
        :class="{ 'cp__item--active': player.currentChapterIndex.value === index }"
        :aria-current="player.currentChapterIndex.value === index ? 'true' : undefined"
        :disabled="!player.canSeek.value"
        @click="player.seek(chapter.startTime)"
      >
        <!-- Thumbnail -->
        <div v-if="hasAnyThumbnail" class="cp__thumbnail">
          <img
            v-if="chapter.thumbnailUrl"
            :src="chapter.thumbnailUrl"
            :alt="chapter.title"
            class="cp__thumbnail-img"
            loading="lazy"
          />
          <div v-else class="cp__thumbnail-placeholder">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
            </svg>
          </div>
          <span class="cp__time-badge">{{ formatTime(chapter.startTime) }}</span>
        </div>

        <!-- Content -->
        <div class="cp__content">
          <div class="cp__header">
            <span class="cp__index">{{ index + 1 }}</span>
            <span v-if="!hasAnyThumbnail" class="cp__time">{{ formatTime(chapter.startTime) }}</span>
          </div>
          <span class="cp__title">{{ chapter.title }}</span>
        </div>
      </button>
    </div>
    <div v-else class="cp__placeholder">
      {{ t('player.info.noChapters') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { UseVideoPlayerReturn } from '../../composables/useVideoPlayer'
import { useVideoI18n } from '../../i18n'
import { formatTime } from '../../utils/time'

interface Props {
  player: UseVideoPlayerReturn
}

const props = defineProps<Props>()
const { t } = useVideoI18n()

const hasAnyThumbnail = computed(() =>
  props.player.chapters.value.some((ch) => ch.thumbnailUrl),
)
</script>

<style scoped lang="scss">
@use '../../styles/mixins' as *;

.cp {
  padding: 8px 0;
  overflow-x: hidden;

  &__items {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px 10px;
    overflow-y: auto;
    @include custom-scrollbar;
  }

  // ── Item ──
  &__item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 10px;
    cursor: pointer;
    background: none;
    border: none;
    border-radius: 8px;
    text-align: left;
    font-family: inherit;
    width: 100%;
    color: inherit;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: var(--vp-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }

    // A table of contents, not a way in: the viewer may not move the playhead (`seeking`).
    &:disabled {
      cursor: default;
    }

    &:disabled:not(.cp__item--active):hover {
      background-color: transparent;
    }

    &--active {
      background-color: var(--vp-surface-hover);

      .cp__title {
        color: var(--vp-text-primary);
      }

      .cp__index {
        color: var(--vp-text-primary);
        background: var(--vp-surface-hover);
        border-color: var(--vp-border-default);
      }

      .cp__time {
        color: var(--vp-text-primary);
        background: var(--vp-surface-hover);
        border-color: var(--vp-border-default);
      }
    }
  }

  // ── Thumbnail ──
  &__thumbnail {
    position: relative;
    width: 80px;
    aspect-ratio: 16 / 9;
    border-radius: 6px;
    overflow: hidden;
    background-color: var(--vp-surface-subtle);
    flex-shrink: 0;
  }

  &__thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__thumbnail-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--vp-surface-hover);
    color: var(--vp-text-muted);

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &__time-badge {
    position: absolute;
    bottom: 2px;
    right: 2px;
    background-color: rgba(0, 0, 0, 0.8);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    padding: 1px 4px;
    border-radius: 3px;
    line-height: 1.3;
  }

  // ── Content ──
  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
    padding-top: 1px;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__index {
    font-size: 0.75rem;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    // text-muted fell under 4.5:1. Secondary rather than tertiary: this badge
    // sits on surface-subtle inside a row that turns surface-hover, and
    // tertiary on that stack is ~4.7:1 over a bright frame.
    color: var(--vp-text-secondary);
    min-width: 20px;
    padding: 2px 6px;
    background: var(--vp-surface-subtle);
    border: 1px solid var(--vp-border-subtle);
    border-radius: 4px;
    text-align: center;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  }

  &__time {
    font-size: 0.75rem;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    // Same as the index badge above.
    color: var(--vp-text-secondary);
    padding: 2px 6px;
    background: var(--vp-surface-subtle);
    border: 1px solid var(--vp-border-subtle);
    border-radius: 4px;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  }

  &__title {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--vp-text-tertiary);
    line-height: 1.4;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    transition: color 0.15s ease;
  }

  // ── Empty ──
  &__placeholder {
    padding: 24px 16px;
    text-align: center;
    // text-ghost (0.3 alpha) was ~2:1 in both themes.
    color: var(--vp-text-tertiary);
    font-size: 0.8125rem;
    font-style: italic;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cp__item,
  .cp__index,
  .cp__time,
  .cp__title {
    transition: none;
  }
}
</style>
